import React, {
  ChangeEvent,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  productApi,
  ProductImagePayload,
} from "../api/productApi";
import { getImageUrl } from "../utils/getImageUrl";
import "../styles/ProductMediaLibraryPage.css";

type ProductImage = ProductImagePayload;

type AdminProduct = {
  id: string;
  name: string;
  sku: string;
  slug: string;
  parentCategoryName?: string;
  categoryName: string;
  images: ProductImage[];
};

type Props = {
  onBack?: () => void;
};

type MediaItem = {
  productId: string;
  productName: string;
  sku: string;
  parentCategoryName?: string;
  categoryName: string;
  image: ProductImage;
};

const normalizeImages = (
  images: ProductImage[]
): ProductImage[] => {
  const ordered = [...(images || [])].sort(
    (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
  );

  const mainIndex = ordered.findIndex((image) => image.isMain);
  const resolvedMainIndex =
    ordered.length === 0 ? -1 : mainIndex >= 0 ? mainIndex : 0;

  return ordered.map((image, index) => ({
    ...image,
    isMain: index === resolvedMainIndex,
    sortOrder: index + 1,
  }));
};

const ProductMediaLibraryPage: React.FC<Props> = ({
  onBack,
}) => {
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [productFilter, setProductFilter] = useState("all");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [uploadProductId, setUploadProductId] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");

    try {
      const data = await productApi.getAllAdminProducts();

      const nextProducts: AdminProduct[] = (data.products || []).map(
        (product: AdminProduct) => ({
          ...product,
          images: normalizeImages(product.images || []),
        })
      );

      setProducts(nextProducts);

      if (!uploadProductId && nextProducts.length > 0) {
        setUploadProductId(nextProducts[0].id);
      }
    } catch (requestError) {
      const err = requestError as Error;
      setError(err.message || "Không tải được thư viện hình ảnh.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mediaItems = useMemo<MediaItem[]>(() => {
    const keyword = search.trim().toLowerCase();

    return products
      .flatMap((product) =>
        (product.images || []).map((image) => ({
          productId: product.id,
          productName: product.name,
          sku: product.sku,
          parentCategoryName:
            product.parentCategoryName,
          categoryName:
            product.categoryName,
          image,
        }))
      )
      .filter((item) => {
        const matchProduct =
          productFilter === "all" || item.productId === productFilter;

        if (!matchProduct) {
          return false;
        }

        if (!keyword) {
          return true;
        }

        const haystack = [
          item.productName,
          item.sku,
          item.parentCategoryName,
          item.categoryName,
          item.image.fileName,
          item.image.alt,
          item.image.url,
        ]
          .join(" ")
          .toLowerCase();

        return haystack.includes(keyword);
      });
  }, [products, productFilter, search]);

  const selectedItem = useMemo(() => {
    if (!selectedKey) {
      return null;
    }

    return mediaItems.find(
      (item) => `${item.productId}::${item.image.url}` === selectedKey
    ) || null;
  }, [mediaItems, selectedKey]);

  const replaceProductImages = (
    productId: string,
    images: ProductImage[]
  ) => {
    setProducts((previous) =>
      previous.map((product) =>
        product.id === productId
          ? {
              ...product,
              images: normalizeImages(images),
            }
          : product
      )
    );
  };

  const saveImages = async (
    productId: string,
    images: ProductImage[],
    successMessage: string
  ) => {
    setSaving(true);
    setError("");
    setMessage("");

    try {
      const normalized = normalizeImages(images);
      const data = await productApi.updateProductImages(
        productId,
        normalized
      );

      replaceProductImages(
        productId,
        data.product?.images || normalized
      );

      setMessage(successMessage);
    } catch (requestError) {
      const err = requestError as Error;
      setError(err.message || "Không thể cập nhật hình ảnh.");
    } finally {
      setSaving(false);
    }
  };

  const handleSetMain = async (item: MediaItem) => {
    const product = products.find((entry) => entry.id === item.productId);

    if (!product) {
      return;
    }

    const next = product.images.map((image) => ({
      ...image,
      isMain: image.url === item.image.url,
    }));

    await saveImages(
      item.productId,
      next,
      "Đã đặt ảnh đại diện cho sản phẩm."
    );
  };

  const handleDelete = async (item: MediaItem) => {
    const ok = window.confirm(
      `Xóa ảnh "${item.image.fileName || item.image.url}" khỏi "${item.productName}"?`
    );

    if (!ok) {
      return;
    }

    const product = products.find((entry) => entry.id === item.productId);

    if (!product) {
      return;
    }

    const next = product.images.filter(
      (image) => image.url !== item.image.url
    );

    await saveImages(
      item.productId,
      next,
      "Đã xóa ảnh khỏi sản phẩm."
    );

    setSelectedKey(null);
  };

  const handleMove = async (
    item: MediaItem,
    direction: -1 | 1
  ) => {
    const product = products.find((entry) => entry.id === item.productId);

    if (!product) {
      return;
    }

    const ordered = normalizeImages(product.images);
    const currentIndex = ordered.findIndex(
      (image) => image.url === item.image.url
    );
    const targetIndex = currentIndex + direction;

    if (
      currentIndex < 0 ||
      targetIndex < 0 ||
      targetIndex >= ordered.length
    ) {
      return;
    }

    const next = [...ordered];
    [next[currentIndex], next[targetIndex]] = [
      next[targetIndex],
      next[currentIndex],
    ];

    await saveImages(
      item.productId,
      next,
      "Đã thay đổi thứ tự hình ảnh."
    );
  };

  const handleUpload = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";

    if (!uploadProductId) {
      setError("Vui lòng chọn sản phẩm cần gán ảnh.");
      return;
    }

    if (files.length === 0) {
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const data = await productApi.addProductImages(
        uploadProductId,
        files
      );

      replaceProductImages(
        uploadProductId,
        data.product?.images || []
      );

      setMessage(`Đã thêm ${files.length} ảnh vào sản phẩm.`);
    } catch (requestError) {
      const err = requestError as Error;
      setError(err.message || "Upload hình ảnh thất bại.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="kj-media-page">
      <header className="kj-media-header">
        <div>
          <span className="kj-media-eyebrow">Product Media Library</span>
          <h1>Quản lý hình ảnh sản phẩm</h1>
          <p>
            Tìm kiếm, upload, đặt ảnh chính, đổi thứ tự và xóa ảnh
            mà không cần mở từng form sản phẩm.
          </p>
        </div>

        <div className="kj-media-header-actions">
          {onBack && (
            <button
              type="button"
              className="kj-media-btn kj-media-btn--outline"
              onClick={onBack}
            >
              ← Danh sách sản phẩm
            </button>
          )}
        </div>
      </header>

      <section className="kj-media-upload-card">
        <div>
          <strong>Upload ảnh vào sản phẩm</strong>
          <span>
            Chọn sản phẩm trước, sau đó có thể chọn nhiều ảnh cùng lúc.
          </span>
        </div>

        <select
          value={uploadProductId}
          onChange={(event) => setUploadProductId(event.target.value)}
          disabled={saving || loading}
        >
          <option value="">Chọn sản phẩm</option>
          {products.map((product) => (
            <option key={product.id} value={product.id}>
              {product.sku ? `${product.sku} - ` : ""}
              {product.name}
            </option>
          ))}
        </select>

        <label
          className={`kj-media-btn kj-media-btn--gold ${
            saving ? "is-disabled" : ""
          }`}
        >
          + Upload nhiều ảnh
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleUpload}
            disabled={saving || loading}
          />
        </label>
      </section>

      {(message || error) && (
        <div
          className={`kj-media-alert ${
            error ? "is-error" : "is-success"
          }`}
        >
          {error || message}
        </div>
      )}

      <section className="kj-media-toolbar">
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Tìm theo SKU, tên sản phẩm, tên file..."
        />

        <select
          value={productFilter}
          onChange={(event) => {
            setProductFilter(event.target.value);
            setSelectedKey(null);
          }}
        >
          <option value="all">Tất cả sản phẩm</option>
          {products.map((product) => (
            <option key={product.id} value={product.id}>
              {product.sku ? `${product.sku} - ` : ""}
              {product.name}
            </option>
          ))}
        </select>

        <div className="kj-media-count">
          <strong>{mediaItems.length}</strong>
          <span>ảnh</span>
        </div>
      </section>

      {loading ? (
        <div className="kj-media-state">Đang tải thư viện ảnh...</div>
      ) : mediaItems.length === 0 ? (
        <div className="kj-media-state">
          <h2>Chưa có hình ảnh phù hợp</h2>
          <p>Upload ảnh mới hoặc thay đổi điều kiện tìm kiếm.</p>
        </div>
      ) : (
        <div className="kj-media-layout">
          <section className="kj-media-grid">
            {mediaItems.map((item) => {
              const key = `${item.productId}::${item.image.url}`;
              const active = selectedKey === key;

              return (
                <button
                  type="button"
                  key={key}
                  className={`kj-media-card ${active ? "is-active" : ""}`}
                  onClick={() => setSelectedKey(key)}
                >
                  <div className="kj-media-card-image">
                    <img
                      src={getImageUrl(item.image.url)}
                      alt={item.image.alt || item.productName}
                      loading="lazy"
                    />

                    {item.image.isMain && (
                      <span className="kj-media-main-badge">★ Ảnh chính</span>
                    )}

                    <span className="kj-media-order">
                      #{item.image.sortOrder || 1}
                    </span>
                  </div>

                  <div className="kj-media-card-copy">
                    <strong>{item.productName}</strong>
                    <span>{item.sku || "Chưa có SKU"}</span>
                    <small>{item.image.fileName || item.image.url}</small>
                  </div>
                </button>
              );
            })}
          </section>

          <aside className="kj-media-detail">
            {!selectedItem ? (
              <div className="kj-media-detail-empty">
                Chọn một ảnh để quản lý.
              </div>
            ) : (
              <>
                <div className="kj-media-detail-preview">
                  <img
                    src={getImageUrl(selectedItem.image.url)}
                    alt={
                      selectedItem.image.alt ||
                      selectedItem.productName
                    }
                  />
                </div>

                <div className="kj-media-detail-copy">
                  <span>Sản phẩm</span>
                  <strong>{selectedItem.productName}</strong>

                  <span>SKU</span>
                  <strong>{selectedItem.sku || "—"}</strong>

                  <span>Danh mục</span>
                  <strong>
                    {selectedItem.parentCategoryName
                      ? `${selectedItem.parentCategoryName} › ${selectedItem.categoryName || ""}`
                      : selectedItem.categoryName || "—"}
                  </strong>

                  <span>File</span>
                  <strong>
                    {selectedItem.image.fileName ||
                      selectedItem.image.url}
                  </strong>

                  <span>Alt text</span>
                  <strong>{selectedItem.image.alt || "—"}</strong>
                </div>

                <div className="kj-media-detail-actions">
                  <button
                    type="button"
                    onClick={() => handleSetMain(selectedItem)}
                    disabled={saving || selectedItem.image.isMain}
                  >
                    ★ Đặt làm ảnh chính
                  </button>

                  <div className="kj-media-order-actions">
                    <button
                      type="button"
                      onClick={() => handleMove(selectedItem, -1)}
                      disabled={saving}
                    >
                      ↑ Lên
                    </button>

                    <button
                      type="button"
                      onClick={() => handleMove(selectedItem, 1)}
                      disabled={saving}
                    >
                      ↓ Xuống
                    </button>
                  </div>

                  <button
                    type="button"
                    className="danger"
                    onClick={() => handleDelete(selectedItem)}
                    disabled={saving}
                  >
                    Xóa ảnh
                  </button>
                </div>
              </>
            )}
          </aside>
        </div>
      )}
    </div>
  );
};

export default ProductMediaLibraryPage;
